import { GAME_CONFIG, VOWELS } from './config/gameConfig';
import {
  createModifierLookup,
  DEFAULT_TILE_MODIFIER_ASSIGNMENTS,
  getTileModifierMeta,
  normalizeTileModifiers,
  TILE_MODIFIER_DEFINITIONS,
} from './config/shopOptions';

export const BOARD_SIZE = GAME_CONFIG.boardSize;
export const BOARD_LETTERS = 'ABCDEFGHIJKLMNO'.split('');

const toCoord = (row, col) => `${BOARD_LETTERS[col]}${row + 1}`;
const toTileId = (row, col) => `${row},${col}`;

export const parseTileId = (tileId) => {
  const raw = String(tileId || '').trim();

  const indexMatch = raw.match(/^(\d{1,2}),(\d{1,2})$/);
  if (indexMatch) {
    const row = Number(indexMatch[1]);
    const col = Number(indexMatch[2]);
    if (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
      return { row, col };
    }
  }

  const coordMatch = raw.toUpperCase().match(/^([A-O])(\d{1,2})$/);
  if (coordMatch) {
    const col = BOARD_LETTERS.indexOf(coordMatch[1]);
    const row = Number(coordMatch[2]) - 1;
    if (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
      return { row, col };
    }
  }

  return null;
};

export const normalizeTileId = (tileId) => {
  const parsed = parseTileId(tileId);
  return parsed ? toTileId(parsed.row, parsed.col) : null;
};

const normalizeBoardModifierList = (modifierIds) => {
  const normalized = (Array.isArray(modifierIds) ? modifierIds : [modifierIds])
    .filter(Boolean)
    .map((modifierId) => String(modifierId).trim().toUpperCase())
    .filter(Boolean);

  if (!normalized.length) {
    return ['NORMAL'];
  }

  const unique = [...new Set(normalized)];
  const withoutNormal = unique.filter((modifierId) => modifierId !== 'NORMAL');

  return withoutNormal.length ? withoutNormal : ['NORMAL'];
};

const buildEmptyBoardModifierMap = () => {
  const map = {};

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      map[toTileId(row, col)] = ['NORMAL'];
    }
  }

  return map;
};

const setTileModifiers = (target, tileId, modifierIds) => {
  const normalizedTileId = normalizeTileId(tileId);
  if (!normalizedTileId || !target[normalizedTileId]) {
    return;
  }

  target[normalizedTileId] = normalizeBoardModifierList(modifierIds);
};

const letterMultiplierEffect = (multiplier) => (scoreState) => ({
  ...scoreState,
  letterMultiplier: (scoreState.letterMultiplier || 1) * multiplier,
});

const wordMultiplierEffect = (multiplier) => (scoreState) => ({
  ...scoreState,
  wordMultiplier: (scoreState.wordMultiplier || 1) * multiplier,
});

const passthroughEffect = (scoreState) => scoreState;

const letterAdditiveEffect = (value) => (scoreState) => ({
  ...scoreState,
  letterBonus: (scoreState.letterBonus || 0) + value,
});

const targetLetterMultiplierEffect = (targetLetter, multiplier) => (scoreState, context) => {
  if ((context?.letter || '').toUpperCase() !== String(targetLetter).toUpperCase()) {
    return scoreState;
  }

  return {
    ...scoreState,
    letterMultiplier: (scoreState.letterMultiplier || 1) * multiplier,
  };
};

export const MODIFIER_EFFECT_HELPERS = {
  letterMultiplierEffect,
  wordMultiplierEffect,
  letterAdditiveEffect,
  targetLetterMultiplierEffect,
};

const buildClassicBase = () => {
  const map = buildEmptyBoardModifierMap();

  // Triple Word
  setTileModifiers(map, '0,0', ['TW']);
  setTileModifiers(map, '0,7', ['TW']);
  setTileModifiers(map, '0,14', ['TW']);
  setTileModifiers(map, '7,0', ['TW']);
  setTileModifiers(map, '7,14', ['TW']);
  setTileModifiers(map, '14,0', ['TW']);
  setTileModifiers(map, '14,7', ['TW']);
  setTileModifiers(map, '14,14', ['TW']);

  // Double Word
  setTileModifiers(map, '1,1', ['DW']);
  setTileModifiers(map, '2,2', ['DW']);
  setTileModifiers(map, '3,3', ['DW']);
  setTileModifiers(map, '4,4', ['DW']);
  setTileModifiers(map, '7,7', ['DW']);
  setTileModifiers(map, '10,10', ['DW']);
  setTileModifiers(map, '11,11', ['DW']);
  setTileModifiers(map, '12,12', ['DW']);
  setTileModifiers(map, '13,13', ['DW']);
  setTileModifiers(map, '1,13', ['DW']);
  setTileModifiers(map, '2,12', ['DW']);
  setTileModifiers(map, '3,11', ['DW']);
  setTileModifiers(map, '4,10', ['DW']);
  setTileModifiers(map, '10,4', ['DW']);
  setTileModifiers(map, '11,3', ['DW']);
  setTileModifiers(map, '12,2', ['DW']);
  setTileModifiers(map, '13,1', ['DW']);

  // Triple Letter
  setTileModifiers(map, '1,5', ['TL']);
  setTileModifiers(map, '1,9', ['TL']);
  setTileModifiers(map, '5,1', ['TL']);
  setTileModifiers(map, '5,5', ['TL']);
  setTileModifiers(map, '5,9', ['TL']);
  setTileModifiers(map, '5,13', ['TL']);
  setTileModifiers(map, '9,1', ['TL']);
  setTileModifiers(map, '9,5', ['TL']);
  setTileModifiers(map, '9,9', ['TL']);
  setTileModifiers(map, '9,13', ['TL']);
  setTileModifiers(map, '13,5', ['TL']);
  setTileModifiers(map, '13,9', ['TL']);

  // Double Letter
  setTileModifiers(map, '0,3', ['DL']);
  setTileModifiers(map, '0,11', ['DL']);
  setTileModifiers(map, '2,6', ['DL']);
  setTileModifiers(map, '2,8', ['DL']);
  setTileModifiers(map, '3,0', ['DL']);
  setTileModifiers(map, '3,7', ['DL']);
  setTileModifiers(map, '3,14', ['DL']);
  setTileModifiers(map, '6,2', ['DL']);
  setTileModifiers(map, '6,6', ['DL']);
  setTileModifiers(map, '6,8', ['DL']);
  setTileModifiers(map, '6,12', ['DL']);
  setTileModifiers(map, '7,3', ['DL']);
  setTileModifiers(map, '7,11', ['DL']);
  setTileModifiers(map, '8,2', ['DL']);
  setTileModifiers(map, '8,6', ['DL']);
  setTileModifiers(map, '8,8', ['DL']);
  setTileModifiers(map, '8,12', ['DL']);
  setTileModifiers(map, '11,0', ['DL']);
  setTileModifiers(map, '11,7', ['DL']);
  setTileModifiers(map, '11,14', ['DL']);
  setTileModifiers(map, '12,6', ['DL', 'DW']);
  setTileModifiers(map, '12,8', ['DL']);
  setTileModifiers(map, '14,3', ['DL']);
  setTileModifiers(map, '14,11', ['DL']);

  return map;
};

const CLASSIC_BASE_MODIFIERS = buildClassicBase();

const buildBaseBoardTiles = () => {
  const tiles = {};

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      const id = toTileId(row, col);
      tiles[id] = {
        id,
        coord: toCoord(row, col),
        row,
        col,
        modifiers: CLASSIC_BASE_MODIFIERS[id] || ['NORMAL'],
      };
    }
  }

  return tiles;
};

const BASE_BOARD_TILES = buildBaseBoardTiles();

export const MODIFIER_DEFINITIONS = {
  NORMAL: {
    label: '',
    description: 'No modifier effect.',
    colorKey: 'normal',
    applyEffect: passthroughEffect,
    onLetterScore: passthroughEffect,
  },
  DL: {
    label: 'DL',
    description: 'Doubles the letter score on this square.',
    colorKey: 'dl',
    applyEffect: letterMultiplierEffect(2),
    onLetterScore: letterMultiplierEffect(2),
  },
  TL: {
    label: 'TL',
    description: 'Triples the letter score on this square.',
    colorKey: 'tl',
    applyEffect: letterMultiplierEffect(3),
    onLetterScore: letterMultiplierEffect(3),
  },
  DW: {
    label: 'DW',
    description: 'Doubles the total word score.',
    colorKey: 'dw',
    applyEffect: wordMultiplierEffect(2),
    onLetterScore: wordMultiplierEffect(2),
  },
  TW: {
    label: 'TW',
    description: 'Triples the total word score.',
    colorKey: 'tw',
    applyEffect: wordMultiplierEffect(3),
    onLetterScore: wordMultiplierEffect(3),
  },
  '3L': {
    label: '3L',
    description: 'Triples the letter score.',
    colorKey: 'tl',
    applyEffect: letterMultiplierEffect(3),
    onLetterScore: letterMultiplierEffect(3),
  },
  '3W': {
    label: '3W',
    description: 'Triples the word score.',
    colorKey: 'tw',
    applyEffect: wordMultiplierEffect(3),
    onLetterScore: wordMultiplierEffect(3),
  },
  '4L': {
    label: '4L',
    description: 'Quadruples the letter score.',
    colorKey: 'quadLetter',
    applyEffect: letterMultiplierEffect(4),
    onLetterScore: letterMultiplierEffect(4),
  },
  '4W': {
    label: '4W',
    description: 'Quadruples the word score.',
    colorKey: 'quadWord',
    applyEffect: wordMultiplierEffect(4),
    onLetterScore: wordMultiplierEffect(4),
  },
  // Placeholder examples for future extensibility:
  // '0L': { label: '0L', colorKey: 'normal', onLetterScore: letterMultiplierEffect(0) },
  // '10E': { label: '10E', colorKey: 'quadLetter', onLetterScore: targetLetterMultiplierEffect('E', 10) },
};

export { TILE_MODIFIER_DEFINITIONS, DEFAULT_TILE_MODIFIER_ASSIGNMENTS, getTileModifierMeta };

export const getTileModifiers = (letter, overrides = {}) => {
  const upperLetter = String(letter || '').toUpperCase();

  if (overrides && Object.prototype.hasOwnProperty.call(overrides, upperLetter)) {
    return normalizeTileModifiers(overrides[upperLetter]);
  }

  if (DEFAULT_TILE_MODIFIER_ASSIGNMENTS[upperLetter]) {
    return normalizeTileModifiers(DEFAULT_TILE_MODIFIER_ASSIGNMENTS[upperLetter]);
  }

  return normalizeTileModifiers(VOWELS.includes(upperLetter) ? { score: 1 } : null);
};

export const applyBoardModifierToSquare = (boardTiles, tileIdOrCoord, modifierId) => {
  const normalizedTileId = normalizeTileId(tileIdOrCoord);
  if (!normalizedTileId || !boardTiles[normalizedTileId]) {
    return boardTiles;
  }

  const existing = boardTiles[normalizedTileId].modifiers;
  const next = normalizeBoardModifierList([...existing, modifierId]);

  return {
    ...boardTiles,
    [normalizedTileId]: {
      ...boardTiles[normalizedTileId],
      modifiers: next,
    },
  };
};

export const createBoardModifiers = (overrides = {}) => {
  const boardTiles = createBoardTiles(overrides);

  return Object.values(boardTiles).reduce((acc, tile) => {
    acc[tile.coord] = tile.modifiers;
    return acc;
  }, {});
};

export const createBoardTiles = (overrides = {}) => {
  const merged = Object.values(BASE_BOARD_TILES).reduce((acc, tile) => {
    acc[tile.id] = {
      ...tile,
      modifiers: [...tile.modifiers],
    };
    return acc;
  }, {});

  Object.entries(overrides).forEach(([tileId, value]) => {
    const normalizedTileId = normalizeTileId(tileId);
    if (!normalizedTileId || !merged[normalizedTileId]) {
      return;
    }

    const modifierIds =
      value && typeof value === 'object' && !Array.isArray(value)
        ? value.modifiers
        : value;

    merged[normalizedTileId] = {
      ...merged[normalizedTileId],
      modifiers: normalizeBoardModifierList(modifierIds),
    };
  });

  return merged;
};

export const getTileId = (row, col) => toTileId(row, col);

export const getSquareModifiers = (boardTiles, tileIdOrCoord) => {
  const normalizedTileId = normalizeTileId(tileIdOrCoord);
  if (!normalizedTileId || !boardTiles[normalizedTileId]) {
    return ['NORMAL'];
  }

  return normalizeBoardModifierList(boardTiles[normalizedTileId].modifiers);
};

export const getModifierMeta = createModifierLookup(MODIFIER_DEFINITIONS, 'board modifier');

export const applySquareEffects = (modifierIds, initialScoreState = {}) =>
  normalizeBoardModifierList(modifierIds).reduce((scoreState, modifierId) => {
    const modifier = getModifierMeta(modifierId);
    const effect = modifier.onLetterScore || modifier.applyEffect || passthroughEffect;
    return effect(scoreState);
  }, initialScoreState);

export const applyTileEffects = (tileModifiers, initialScoreState = {}, context = {}) => {
  const extras = Array.isArray(tileModifiers?.extras) ? tileModifiers.extras : [];
  return extras.reduce((scoreState, modifierId) => {
    const modifier = getTileModifierMeta(modifierId);
    const effect = modifier.onLetterScore || modifier.applyEffect || passthroughEffect;
    return effect(scoreState, context);
  }, initialScoreState);
};

export const MODIFIER_META = {
  NORMAL: MODIFIER_DEFINITIONS.NORMAL,
  DL: MODIFIER_DEFINITIONS.DL,
  TL: MODIFIER_DEFINITIONS.TL,
  DW: MODIFIER_DEFINITIONS.DW,
  TW: MODIFIER_DEFINITIONS.TW,
  '3L': MODIFIER_DEFINITIONS['3L'],
  '3W': MODIFIER_DEFINITIONS['3W'],
  '4L': MODIFIER_DEFINITIONS['4L'],
  '4W': MODIFIER_DEFINITIONS['4W'],
};